import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const http = axios.create({ baseURL: API, withCredentials: true });

// Free-plan usage cap: surface the upgrade prompt and route to pricing.
http.interceptors.response.use(
  (r) => r,
  (error) => {
    const { status, data, config } = error.response || {};
    if (status === 402 && !config?.url?.includes("/billing/")) {
      const msg = data?.detail || "Upgrade to Pro to continue.";
      toast.error(msg);
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/pricing")) {
        setTimeout(() => window.location.assign("/pricing"), 1200);
      }
    }
    return Promise.reject(error);
  }
);

const usageChanged = () => window.dispatchEvent(new Event("usage-changed"));


// ---- Auth ----
export const apiRegister = (email, password, name) => http.post("/auth/register", { email, password, name }).then((r) => r.data);
export const apiLogin = (email, password) => http.post("/auth/login", { email, password }).then((r) => r.data);
export const apiGoogle = (session_id) => http.post("/auth/google", { session_id }).then((r) => r.data);
export const apiMe = () => http.get("/auth/me").then((r) => r.data);
export const apiLogout = () => http.post("/auth/logout").then((r) => r.data);

// ---- Billing ----
export const listPlans = () => http.get("/billing/plans").then((r) => r.data);
export const startCheckout = (lookup_key, quantity, origin_url) =>
  http.post("/billing/checkout", { lookup_key, quantity, origin_url }).then((r) => r.data);
export const checkoutStatus = (session_id) => http.get(`/billing/status/${session_id}`).then((r) => r.data);
export const openBillingPortal = (origin_url) => http.post("/billing/portal", { origin_url }).then((r) => r.data);
export const redeemBeta = (code) => http.post("/billing/beta", { code }).then((r) => r.data);

// ---- Admin ----
export const adminUsers = () => http.get("/admin/users").then((r) => r.data);

// ---- Settings / schema versions ----
export const getSettings = () => http.get("/settings").then((r) => r.data);
export const updateSettings = (payload) => http.put("/settings", payload).then((r) => r.data);
export const listSchemaVersions = () => http.get("/schema-versions").then((r) => r.data);

export const listFiles = () => http.get("/files").then((r) => r.data);
export const getFile = (id) => http.get(`/files/${id}`).then((r) => r.data);
export const createFile = (name, content) =>
  http.post("/files", { name, content }).then((r) => r.data);
export const deleteFile = (id) => http.delete(`/files/${id}`).then((r) => r.data);
export const updateAssetField = (file_id, asset_id, field, value) =>
  http.patch(`/files/${file_id}/asset/${asset_id}`, { field, value }).then((r) => r.data);
export const getFileDiff = (id) => http.get(`/files/${id}/diff`).then((r) => r.data);
export const loadSamples = () => http.post("/samples/load").then((r) => r.data);

export const listProjects = () => http.get("/projects").then((r) => r.data);
export const createProject = (payload) => http.post("/projects", payload).then((r) => r.data);
export const deleteProject = (id) => http.delete(`/projects/${id}`).then((r) => r.data);

export const runCompare = (baseline_id, current_id, name) =>
  http.post("/compare", { baseline_id, current_id, name }).then((r) => r.data);
export const listComparisons = () => http.get("/comparisons").then((r) => r.data);
export const getComparison = (id) =>
  http.get(`/comparisons/${id}`).then((r) => r.data);
export const deleteComparison = (id) =>
  http.delete(`/comparisons/${id}`).then((r) => r.data);

export const listStandards = () => http.get("/standards").then((r) => r.data);
export const listCategories = () => http.get("/categories").then((r) => r.data);
export const listToleranceProfiles = () => http.get("/tolerance-profiles").then((r) => r.data);
export const createToleranceProfile = (payload) => http.post("/tolerance-profiles", payload).then((r) => r.data);
export const deleteToleranceProfile = (id) => http.delete(`/tolerance-profiles/${id}`).then((r) => r.data);
export const previewQA = (design_id, survey_id, standard_id, links, opts = {}) =>
  http.post("/qa/preview", { design_id, survey_id, standard_id, links, ...opts }).then((r) => r.data);
export const runQA = (design_id, survey_id, standard_id, links, name, opts = {}) =>
  http.post("/qa", { design_id, survey_id, standard_id, links, name, ...opts }).then((r) => r.data);
export const listQA = () => http.get("/qa").then((r) => r.data);
export const getQA = (id) => http.get(`/qa/${id}`).then((r) => r.data);
export const deleteQA = (id) => http.delete(`/qa/${id}`).then((r) => r.data);

export const getSchema = () => http.get("/schema").then((r) => r.data);
export const suggestMatches = (design_id, survey_id, links, bounds) =>
  http.post("/qa/suggest", { design_id, survey_id, links, bounds }).then((r) => r.data);
export const validateFile = (file_id, version) =>
  http.post("/validate", { file_id, version }).then((r) => r.data);

export const profileLines = (file_id, asset_ids, opts = {}) =>
  http.post("/profile", { file_id, asset_ids, ...opts }).then((r) => r.data);

export const listSurfaces = () => http.get("/surfaces").then((r) => r.data);
export const createSurface = (name, role, filename, content) =>
  http.post("/surfaces", { name, role, filename, content }).then((r) => r.data);
export const deleteSurface = (id) => http.delete(`/surfaces/${id}`).then((r) => r.data);
export const computeCrossSection = (payload) =>
  http.post("/cross-section", payload).then((r) => r.data);
export const computeVolume = (base_surface_id, top_surface_id, spacing) =>
  http.post("/volumes", { base_surface_id, top_surface_id, spacing }).then((r) => r.data);

export const saveLinkRules = (survey_id, links) =>
  http.post("/linkrules", { survey_id, links }).then((r) => r.data);
export const getLinkRules = (survey_id) =>
  http.get("/linkrules", { params: { survey_id } }).then((r) => r.data);

const downloadBlob = (data, filename) => {
  const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
export const downloadQAPdf = (payload, filename) =>
  http.post("/qa/report", payload, { responseType: "blob" }).then((r) => { usageChanged(); return downloadBlob(r.data, filename); });
export const downloadValidationPdf = (file_id, filename) =>
  http.post("/validate/report", { file_id }, { responseType: "blob" }).then((r) => { usageChanged(); return downloadBlob(r.data, filename); });
export const detectStrings = (file_id) =>
  http.post("/profile/strings", { file_id }).then((r) => r.data);
export const findAssociatedString = (file_id, other_file_id, asset_ids, corridor_width) =>
  http.post("/profile/associated", { file_id, other_file_id, asset_ids, corridor_width }).then((r) => r.data);
export const downloadProfilePdf = (payload, filename) =>
  http.post("/profile/report", payload, { responseType: "blob" }).then((r) => { usageChanged(); return downloadBlob(r.data, filename); });
export const downloadSubmissionPack = (payload, filename) =>
  http.post("/submission-pack", payload, { responseType: "blob" }).then((r) => { usageChanged(); return downloadBlob(r.data, filename); });

export const setLgaSchema = (standard_id) =>
  http.put("/auth/lga", { standard_id }).then((r) => r.data);
export const listValidations = () => http.get("/validations").then((r) => r.data);
export const getValidation = (id) => http.get(`/validations/${id}`).then((r) => r.data);
export const deleteValidation = (id) =>
  http.delete(`/validations/${id}`).then((r) => r.data);
